import { MouseEvent, ReactElement, useState } from "react";
import { t } from "@lingui/core/macro";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { curveMonotoneX } from "@visx/curve";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { useParentSize } from "@visx/responsive";
import { scaleLinear, scaleTime, scaleUtc } from "@visx/scale";
import { LinePath } from "@visx/shape";
import { TooltipWithBounds, useTooltip } from "@visx/tooltip";
import { Temporal } from "temporal-polyfill";

import { type LogEvent, type LogLevel } from "_/data/machines";
import { defaultFontName, palette } from "_/style";
import {
  type DisplayTimeZone,
  dateTimeString,
  timeOnlyString,
} from "_/utils/datetime";

import * as S from "./styled";

const MARGIN = { top: 8, right: 8, bottom: 24, left: 40 } as const;
const BUCKET_COUNT = 60;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Minimum distance that must be dragged to select a range, so that clicks
// don't select a tiny range by accident.
const MIN_DRAG_PX = 10;

type Buckets = {
  bucketMs: number;
  starts: number[];
  countsByLevel: Map<LogLevel, number[]>;
};

// Counts events per level in `BUCKET_COUNT` equally sized time buckets that
// span the queried time range.
function bucketEvents(
  events: LogEvent[],
  levels: LogLevel[],
  fromMs: number,
  toMs: number
): Buckets {
  const bucketMs = Math.max(1, (toMs - fromMs) / BUCKET_COUNT);
  const starts = Array.from(
    { length: BUCKET_COUNT },
    (_, i) => fromMs + i * bucketMs
  );
  const countsByLevel = new Map(
    levels.map((level) => [
      level,
      Array.from({ length: BUCKET_COUNT }, () => 0),
    ])
  );

  for (const event of events) {
    // Event timestamps are epoch microseconds.
    const offsetMs = event.timestamp / 1000 - fromMs;
    const index = Math.min(BUCKET_COUNT - 1, Math.floor(offsetMs / bucketMs));
    const counts = countsByLevel.get(event.level);
    if (counts && index >= 0) {
      counts[index] += 1;
    }
  }

  return { bucketMs, starts, countsByLevel };
}

type TooltipData = { bucketIndex: number };

type LogChartProps = {
  events: LogEvent[];
  levels: LogLevel[];
  from: string;
  to: string;
  displayTimeZone: DisplayTimeZone;
  /** Called with the time range selected by dragging across the chart. */
  onRangeSelect: (start: Temporal.Instant, end: Temporal.Instant) => void;
  /** Called when the chart is double-clicked, to reset the time range. */
  onReset: () => void;
};

export const LogChart = ({
  events,
  levels,
  from,
  to,
  displayTimeZone,
  onRangeSelect,
  onReset,
}: LogChartProps): ReactElement => {
  const { parentRef, width, height } = useParentSize();
  const { hideTooltip, showTooltip, tooltipData, tooltipLeft, tooltipOpen } =
    useTooltip<TooltipData>();
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragCurrentX, setDragCurrentX] = useState<number | null>(null);
  const isDragging = dragStartX != null;

  const fromMs = Temporal.Instant.from(from).epochMilliseconds;
  const toMs = Temporal.Instant.from(to).epochMilliseconds;
  const { bucketMs, starts, countsByLevel } = bucketEvents(
    events,
    levels,
    fromMs,
    toMs
  );

  const plotWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotHeight = Math.max(0, height - MARGIN.top - MARGIN.bottom);

  const maxCount = Math.max(1, ...[...countsByLevel.values()].flat());
  // A UTC scale puts ticks on UTC hour and day boundaries instead of local
  // ones, to line up with the UTC tick labels.
  const createTimeScale = displayTimeZone === "utc" ? scaleUtc : scaleTime;
  const xScale = createTimeScale({
    domain: [fromMs, toMs],
    range: [0, plotWidth],
  });
  const yScale = scaleLinear({
    domain: [0, maxCount],
    range: [plotHeight, 0],
    nice: true,
  });
  // Counts are whole numbers, so don't show fractional ticks.
  const yTickValues = yScale.ticks(4).filter((v) => Number.isInteger(v));

  // Plot each bucket's count at the bucket's midpoint.
  const bucketX = (index: number) => xScale(starts[index] + bucketMs / 2);

  const isMultiDay = toMs - fromMs > ONE_DAY_MS;
  const formatTick = (value: Date | { valueOf(): number }) => {
    const ms = value.valueOf();
    return isMultiDay
      ? dateTimeString({ value: ms, timeZone: displayTimeZone })
      : timeOnlyString({ value: ms, timeZone: displayTimeZone });
  };

  function getPlotX(event: MouseEvent<SVGRectElement>): number | null {
    const point = localPoint(event);
    if (!point) {
      return null;
    }
    return Math.min(plotWidth, Math.max(0, point.x - MARGIN.left));
  }

  const plotXToInstant = (plotX: number) =>
    Temporal.Instant.fromEpochMilliseconds(
      xScale.invert(plotX).getTime()
    ).round({ smallestUnit: "second" });

  function handleMouseDown(event: MouseEvent<SVGRectElement>) {
    // Stop the drag from selecting text on the page.
    event.preventDefault();
    const plotX = getPlotX(event);
    setDragStartX(plotX);
    setDragCurrentX(plotX);
    hideTooltip();
  }

  function handleMouseUp() {
    if (dragStartX != null && dragCurrentX != null) {
      const left = Math.min(dragStartX, dragCurrentX);
      const right = Math.max(dragStartX, dragCurrentX);
      if (right - left >= MIN_DRAG_PX) {
        onRangeSelect(plotXToInstant(left), plotXToInstant(right));
      }
    }
    setDragStartX(null);
    setDragCurrentX(null);
  }

  function handleMouseLeave() {
    hideTooltip();
    setDragStartX(null);
    setDragCurrentX(null);
  }

  function handleMouseMove(event: MouseEvent<SVGRectElement>) {
    const plotX = getPlotX(event);
    if (plotX == null) {
      return;
    }
    if (isDragging) {
      setDragCurrentX(plotX);
      return;
    }
    const offsetMs = xScale.invert(plotX).getTime() - fromMs;
    const bucketIndex = Math.min(
      BUCKET_COUNT - 1,
      Math.max(0, Math.floor(offsetMs / bucketMs))
    );
    showTooltip({
      tooltipLeft: bucketX(bucketIndex) + MARGIN.left,
      tooltipTop: 0,
      tooltipData: { bucketIndex },
    });
  }

  const tickFont = `"${defaultFontName}", sans-serif`;
  const tickLabelProps = {
    fill: palette.ironGrey,
    fontSize: 11,
    fontFamily: tickFont,
  };

  const cursorLine = tooltipOpen && tooltipLeft != null && (
    <line
      x1={tooltipLeft - MARGIN.left}
      x2={tooltipLeft - MARGIN.left}
      y1={0}
      y2={plotHeight}
      stroke={palette.ironGrey}
      strokeWidth={1}
      strokeDasharray="3 3"
      pointerEvents="none"
    />
  );

  const selectionRect = dragStartX != null && dragCurrentX != null && (
    <rect
      x={Math.min(dragStartX, dragCurrentX)}
      y={0}
      width={Math.abs(dragCurrentX - dragStartX)}
      height={plotHeight}
      fill={palette.electricBlue}
      fillOpacity={0.15}
      stroke={palette.electricBlue}
      strokeWidth={1}
      pointerEvents="none"
    />
  );

  const tooltip = tooltipOpen && tooltipData && (
    <TooltipWithBounds
      top={MARGIN.top}
      left={tooltipLeft}
      style={{
        position: "absolute",
        pointerEvents: "none",
        background: "none",
        border: "none",
        boxShadow: "none",
        padding: 0,
      }}
    >
      <S.TooltipContent>
        <S.TooltipTime>
          {dateTimeString({
            value: starts[tooltipData.bucketIndex],
            timeZone: displayTimeZone,
          })}
          {" – "}
          {timeOnlyString({
            value: starts[tooltipData.bucketIndex] + bucketMs,
            timeZone: displayTimeZone,
          })}
        </S.TooltipTime>
        {levels.map((level) => (
          <S.TooltipRow key={level}>
            <S.LevelIndicator $level={level} />
            {t`common.log-levels ${level}`}
            {": "}
            {countsByLevel.get(level)?.[tooltipData.bucketIndex] ?? 0}
          </S.TooltipRow>
        ))}
      </S.TooltipContent>
    </TooltipWithBounds>
  );

  return (
    <S.ChartWrapper ref={parentRef}>
      {plotWidth > 0 && plotHeight > 0 && (
        <svg width={width} height={height} style={{ display: "block" }}>
          <Group left={MARGIN.left} top={MARGIN.top}>
            <GridRows
              scale={yScale}
              tickValues={yTickValues}
              width={plotWidth}
              stroke={palette.graniteBlack}
              strokeWidth={1}
            />
            {levels.map((level) => (
              <LinePath
                key={level}
                data={countsByLevel.get(level) ?? []}
                x={(_, index) => bucketX(index)}
                y={(count) => yScale(count)}
                curve={curveMonotoneX}
                stroke={S.levelColor(level)}
                strokeWidth={1.5}
              />
            ))}
            {cursorLine}
            {selectionRect}
            <AxisBottom
              scale={xScale}
              top={plotHeight}
              numTicks={isMultiDay ? 4 : 8}
              stroke={palette.ironGrey}
              tickStroke={palette.ironGrey}
              tickLabelProps={{ ...tickLabelProps, textAnchor: "middle" }}
              tickFormat={formatTick}
            />
            <AxisLeft
              scale={yScale}
              tickValues={yTickValues}
              tickFormat={yScale.tickFormat(4, ",.0f")}
              stroke={palette.ironGrey}
              tickStroke={palette.ironGrey}
              tickLabelProps={{
                ...tickLabelProps,
                textAnchor: "end",
                dx: "-0.25em",
                dy: "0.33em",
              }}
            />
            <rect
              width={plotWidth}
              height={plotHeight}
              fill="transparent"
              style={{ cursor: isDragging ? "ew-resize" : "crosshair" }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onDoubleClick={onReset}
            />
          </Group>
        </svg>
      )}
      {tooltip}
    </S.ChartWrapper>
  );
};

type LogLevelCountsProps = {
  events: LogEvent[];
  levels: LogLevel[];
};

export const LogLevelCounts = ({
  events,
  levels,
}: LogLevelCountsProps): ReactElement => (
  <S.LevelCounts>
    {levels.map((level) => (
      <S.LevelCount key={level}>
        <S.LevelIndicator $level={level} />
        {t`common.log-levels ${level}`}
        <S.LevelCountValue>
          {events.filter((event) => event.level === level).length}
        </S.LevelCountValue>
      </S.LevelCount>
    ))}
  </S.LevelCounts>
);
